import * as React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  FolderOpen, ShieldAlert, Clock, EyeOff, Loader2, Search,
  ArrowUpRight, CheckCircle2, PauseCircle, X, Send, Sparkles,
  BookOpen, Plus, Trash2,
} from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import DetailDrawer from "@/components/DetailDrawer";

const SEV_CHIP = {
  P1: "chip-coral",
  P2: "chip-amber",
  P3: "chip-violet",
  P4: "chip",
};
const STATUS_LABEL = {
  open: "Open",
  in_progress: "In progress",
  waiting_on_employee: "Waiting on employee",
  waiting_on_third_party: "Waiting on third party",
  resolved: "Resolved",
  closed: "Closed",
};
const TERMINAL = new Set(["resolved", "closed"]);

function fmtMs(ms) {
  if (ms == null) return "—";
  const abs = Math.abs(ms);
  const s = Math.floor(abs / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

// ─────────────────────────────────────────────────────────────
// Analytics strip
// ─────────────────────────────────────────────────────────────

function AnalyticsStrip({ analytics, loading }) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3" data-testid="cases-analytics-loading">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="card-panel">
            <div className="p-3 space-y-2">
              <div className="h-3 w-16 rounded bg-brand-hush animate-pulse" />
              <div className="h-6 w-12 rounded bg-brand-hush animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (!analytics) return null;
  const t = analytics.totals || {};
  const bySev = analytics.by_severity || {};
  const cells = [
    { label: "Open", value: t.open ?? 0, tone: "" },
    { label: "P1 + P2", value: (bySev.P1 || 0) + (bySev.P2 || 0), tone: "coral" },
    { label: "SLA breaches", value: t.breaches ?? 0, tone: t.breaches ? "coral" : "" },
    {
      label: "SLA compliance",
      value: analytics.sla_compliance_pct == null ? "—" : `${analytics.sla_compliance_pct}%`,
      tone: "",
    },
    { label: "Deflections", value: t.deflections ?? 0, tone: "" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3" data-testid="cases-analytics">
      {cells.map((c) => (
        <div key={c.label} className="card-panel">
          <div className="p-3">
            <div className="text-[10.5px] uppercase tracking-wider text-brand-muted">{c.label}</div>
            <div
              className={`mono-num text-[22px] font-semibold ${c.tone === "coral" && c.value ? "text-brand-coral" : "text-brand-ink"}`}
              data-testid={`cases-stat-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              {c.value}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Row + list
// ─────────────────────────────────────────────────────────────

function SLAPill({ row }) {
  if (row.sla_remaining_ms == null) return <span className="chip mono-num text-[10.5px]">—</span>;
  if (row.sla_breached) {
    return (
      <span className="chip chip-coral mono-num text-[10.5px]" data-testid={`cases-sla-breached-${row.id}`}>
        breach · {fmtMs(row.sla_remaining_ms)}
      </span>
    );
  }
  if (TERMINAL.has(row.status)) {
    return <span className="chip mono-num text-[10.5px]">closed</span>;
  }
  if (row.sla_paused_at) {
    return (
      <span className="chip chip-amber mono-num text-[10.5px]">
        paused · {fmtMs(row.sla_remaining_ms)}
      </span>
    );
  }
  const hoursLeft = row.sla_remaining_ms / 3600000;
  const cls = hoursLeft < 4 ? "chip-amber" : "chip-violet";
  return (
    <span className={`chip ${cls} mono-num text-[10.5px]`}>
      {fmtMs(row.sla_remaining_ms)} left
    </span>
  );
}

function CaseRow({ row, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(row)}
      className="w-full grid grid-cols-[110px_60px_1fr_140px_130px] items-center gap-3 px-3 py-2 border-b border-brand-rule hover:bg-brand-hush text-left"
      data-testid={`cases-row-${row.id}`}
      aria-label={`Open case ${row.number}: ${row.summary}`}
    >
      <span className="mono-num text-[12.5px] font-semibold text-brand-ink truncate">
        {row.number}
      </span>
      <span className={`chip ${SEV_CHIP[row.severity] || "chip"} mono-num text-[10.5px] justify-self-start`}>
        {row.severity}
      </span>
      <span className="min-w-0">
        <span className="text-[13px] text-brand-ink truncate block">
          {row.is_confidential && (
            <span className="inline-flex items-center gap-1 mr-1.5 text-brand-violet" title="Confidential">
              <EyeOff size={11} />
            </span>
          )}
          {row.summary}
        </span>
        <span className="text-[10.5px] text-brand-muted">
          {row.type.replace(/_/g, " ")} · {fmtDateTime(row.created_at)}
        </span>
      </span>
      <span className="text-[11.5px] text-brand-muted">
        {STATUS_LABEL[row.status] || row.status}
      </span>
      <span className="justify-self-end">
        <SLAPill row={row} />
      </span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// Drawer content — full case detail + timeline + actions
// ─────────────────────────────────────────────────────────────

function CaseDrawer({ companyId, caseId, open, onClose, onChanged, has }) {
  const [detail, setDetail] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [replyBody, setReplyBody] = React.useState("");
  const [replyVis, setReplyVis] = React.useState("both");
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!caseId) return;
    setLoading(true); setError(null);
    try {
      const { data } = await api.get(`/companies/${companyId}/cases/${caseId}`);
      setDetail(data);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't load this case");
    } finally { setLoading(false); }
  }, [companyId, caseId]);

  React.useEffect(() => { if (open) load(); }, [open, load]);

  async function transition(target, reason = "") {
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/cases/${caseId}/status`, {
        target_status: target, reason,
      });
      toast.success(`Moved to ${STATUS_LABEL[target] || target}`);
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't change status");
    } finally { setBusy(false); }
  }

  async function assignSelf() {
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/cases/${caseId}/assign`, {
        assignee_user_id: null, note: "self-pickup",
      });
      // Server treats null as unassign; passing my id via a separate call
      const me = await api.get("/auth/me");
      await api.post(`/companies/${companyId}/cases/${caseId}/assign`, {
        assignee_user_id: me.data.user.id, note: "picked up",
      });
      toast.success("Assigned to you");
      await load(); onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't assign");
    } finally { setBusy(false); }
  }

  async function escalate() {
    const reason = prompt("Why are you escalating?");
    if (!reason || reason.trim().length < 3) return;
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/cases/${caseId}/escalate`, { reason });
      toast.success("Escalated");
      await load(); onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't escalate");
    } finally { setBusy(false); }
  }

  async function postReply() {
    if (!replyBody.trim()) return;
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/cases/${caseId}/messages`, {
        body: replyBody.trim(), visibility: replyVis,
      });
      setReplyBody(""); setReplyVis("both");
      toast.success("Sent");
      await load(); onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't post");
    } finally { setBusy(false); }
  }

  const c = detail?.case;
  const tl = detail?.timeline || [];
  const canManage = has && (has("cases.manage") || has("cases.confidential"));

  return (
    <DetailDrawer
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title={c ? `${c.number} · ${c.summary}` : "Case"}
      subtitle={c ? `${c.type.replace(/_/g, " ")} · ${c.severity} · tier ${c.tier}` : ""}
      testid="case-drawer"
    >
      {loading && (
        <div className="p-6 space-y-3" data-testid="case-drawer-loading">
          <div className="h-3 w-40 rounded bg-brand-hush animate-pulse" />
          <div className="h-4 w-full rounded bg-brand-hush animate-pulse" />
          <div className="h-24 w-full rounded bg-brand-hush animate-pulse" />
        </div>
      )}

      {error && (
        <div className="p-6" data-testid="case-drawer-error">
          <div className="text-[13px] text-brand-ink font-semibold mb-1">Couldn't open this case</div>
          <div className="text-[12px] text-brand-muted mb-3">{error}</div>
          <button className="btn-outline text-[12px]" onClick={load}>Try again</button>
        </div>
      )}

      {!loading && !error && c && (
        <div className="flex-1 flex flex-col min-h-0">
          <div className="p-5 border-b border-brand-rule bg-brand-canvas">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className={`chip ${SEV_CHIP[c.severity]} mono-num text-[11px]`}>{c.severity}</span>
              <span className="chip mono-num text-[10.5px]">tier {c.tier}</span>
              {c.is_confidential && (
                <span className="chip chip-violet mono-num text-[10.5px] inline-flex items-center gap-1">
                  <EyeOff size={11} /> confidential
                </span>
              )}
              {c.is_anonymous && (
                <span className="chip mono-num text-[10.5px]">anonymous</span>
              )}
              <span className="chip mono-num text-[10.5px]">{STATUS_LABEL[c.status]}</span>
              <SLAPill row={c} />
            </div>
            <div className="text-[12px] text-brand-muted mb-2">Raised by {c.raised_by_name || "—"} · {fmtDateTime(c.created_at)}</div>
            {c.detail && (
              <div className="text-[13px] text-brand-ink whitespace-pre-wrap leading-relaxed">{c.detail}</div>
            )}
            {canManage && !TERMINAL.has(c.status) && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {c.status === "open" && (
                  <button className="btn-outline text-[11.5px] h-8" onClick={assignSelf} disabled={busy}
                    data-testid="case-action-pickup">
                    Pick up
                  </button>
                )}
                <button className="btn-outline text-[11.5px] h-8" onClick={() => transition("waiting_on_employee")} disabled={busy}
                  data-testid="case-action-wait-employee">
                  <PauseCircle size={12} /> Wait on employee
                </button>
                <button className="btn-outline text-[11.5px] h-8" onClick={() => transition("in_progress")} disabled={busy}
                  data-testid="case-action-resume">
                  Resume
                </button>
                <button className="btn-outline text-[11.5px] h-8" onClick={escalate} disabled={busy}
                  data-testid="case-action-escalate">
                  <ArrowUpRight size={12} /> Escalate
                </button>
                <button className="btn-violet text-[11.5px] h-8" onClick={() => transition("resolved", "resolved by handler")} disabled={busy}
                  data-testid="case-action-resolve">
                  <CheckCircle2 size={12} /> Resolve
                </button>
              </div>
            )}
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-2" data-testid="case-timeline">
            {tl.map((t) => (
              <TimelineRow key={t.id} entry={t} />
            ))}
            {!tl.length && <div className="text-[12px] text-brand-muted">No activity yet.</div>}
          </div>

          <div className="border-t border-brand-rule bg-card px-5 py-3">
            <textarea
              className="field-input min-h-[56px]"
              placeholder="Write a reply…"
              value={replyBody}
              onChange={(e) => setReplyBody(e.target.value)}
              data-testid="case-reply-input"
            />
            <div className="mt-2 flex items-center justify-between gap-2">
              {canManage ? (
                <select
                  className="field-input !h-8 !w-44 text-[11.5px]"
                  value={replyVis}
                  onChange={(e) => setReplyVis(e.target.value)}
                  data-testid="case-reply-visibility"
                >
                  <option value="both">Reply to raiser + handlers</option>
                  <option value="handlers_only">Handler-only note</option>
                </select>
              ) : <span className="text-[11px] text-brand-muted">Reply visible to handlers</span>}
              <button
                className="btn-violet text-[11.5px] h-8"
                onClick={postReply}
                disabled={busy || !replyBody.trim()}
                data-testid="case-reply-send"
              >
                <Send size={12} /> Send
              </button>
            </div>
          </div>
        </div>
      )}
    </DetailDrawer>
  );
}

function TimelineRow({ entry }) {
  const kind = entry.kind;
  const iconTone = kind === "sla_breach" ? "text-brand-coral"
                  : kind === "escalated" ? "text-brand-violet"
                  : "text-brand-muted";
  return (
    <div className="grid grid-cols-[112px_1fr] gap-3 items-start">
      <div className="text-[11px] text-brand-muted mono-num">{fmtDateTime(entry.created_at)}</div>
      <div className="min-w-0">
        <div className={`text-[10.5px] uppercase tracking-wider ${iconTone}`}>{kind.replace(/_/g, " ")}</div>
        <div className="text-[12.5px] text-brand-ink">
          <span className="font-medium">{entry.actor_name || (entry.actor_role === "raiser" ? "Raiser" : "System")}</span>
          {entry.body ? <> — {entry.body}</> : null}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Raise-case dialog + KB deflection
// ─────────────────────────────────────────────────────────────

const CASE_TYPES = [
  { v: "pay_query",         label: "Pay query" },
  { v: "timesheet_dispute", label: "Timesheet dispute" },
  { v: "hr_general",        label: "HR question" },
  { v: "er_grievance",      label: "Employee relations · grievance" },
  { v: "er_disciplinary",   label: "Employee relations · disciplinary" },
  { v: "safeguarding",      label: "Safeguarding (confidential)" },
  { v: "whistleblowing",    label: "Whistleblowing (confidential)" },
  { v: "abuse_report",      label: "Abuse report (confidential)" },
];

function RaiseDialog({ companyId, open, onClose, onCreated }) {
  const [step, setStep] = React.useState("compose"); // compose | deflect
  const [type, setType] = React.useState("hr_general");
  const [summary, setSummary] = React.useState("");
  const [detail, setDetail] = React.useState("");
  const [suggestions, setSuggestions] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [shownIds, setShownIds] = React.useState([]);

  React.useEffect(() => {
    if (!open) {
      setStep("compose"); setSummary(""); setDetail(""); setSuggestions([]); setShownIds([]);
    }
  }, [open]);

  async function submit(deflection_accepted = false) {
    if (summary.trim().length < 3) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/cases`, {
        type, summary: summary.trim(), detail: detail.trim(),
        reported_flags: [],
        kb_articles_shown: shownIds,
        kb_deflection_accepted: deflection_accepted,
      });
      toast.success(`Case ${data.number} created`);
      onCreated && onCreated(data);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't raise the case");
    } finally { setBusy(false); }
  }

  async function tryDeflect() {
    if (summary.trim().length < 3) { toast.info("Add a short summary first"); return; }
    setBusy(true);
    try {
      const { data } = await api.get(`/companies/${companyId}/kb/match`, {
        params: { q: summary, type, limit: 3 },
      });
      const arts = data.articles || [];
      setSuggestions(arts);
      setShownIds(arts.map((a) => a.id));
      setStep(arts.length ? "deflect" : "compose");
      if (!arts.length) submit(false);
    } catch (e) {
      // If deflection fails, still submit
      submit(false);
    } finally { setBusy(false); }
  }

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-start justify-center pt-16 px-4" role="dialog" aria-modal="true">
      <div className="bg-card rounded-lg shadow-card w-full max-w-lg" data-testid="cases-raise-dialog">
        <div className="px-4 py-3 border-b border-brand-rule flex items-center justify-between">
          <div className="text-[14px] font-semibold text-brand-ink">
            {step === "deflect" ? "Try this first" : "Raise a case"}
          </div>
          <button onClick={onClose} className="btn-ghost h-7 w-7 !p-0" aria-label="Close">
            <X size={14} />
          </button>
        </div>

        {step === "compose" && (
          <div className="p-4 space-y-3">
            <label className="block">
              <span className="text-[11px] text-brand-muted">Type</span>
              <select className="field-input mt-1" value={type} onChange={(e) => setType(e.target.value)}
                data-testid="cases-raise-type">
                {CASE_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] text-brand-muted">Summary</span>
              <input className="field-input mt-1" value={summary} maxLength={200}
                onChange={(e) => setSummary(e.target.value)} placeholder="One-line description of the issue"
                data-testid="cases-raise-summary" autoFocus />
            </label>
            <label className="block">
              <span className="text-[11px] text-brand-muted">Detail (optional)</span>
              <textarea className="field-input mt-1 min-h-[100px]" value={detail} maxLength={8000}
                onChange={(e) => setDetail(e.target.value)} placeholder="Context, dates, amounts, names…"
                data-testid="cases-raise-detail" />
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <button className="btn-outline text-[12px] h-8" onClick={onClose}>Cancel</button>
              <button
                className="btn-violet text-[12px] h-8"
                disabled={busy || summary.trim().length < 3}
                onClick={tryDeflect}
                data-testid="cases-raise-submit"
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                Continue
              </button>
            </div>
          </div>
        )}

        {step === "deflect" && (
          <div className="p-4 space-y-3">
            <div className="text-[12.5px] text-brand-muted">
              A few articles that might already answer this. Read one — you might not need a case.
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {suggestions.map((a) => (
                <details key={a.id} className="border border-brand-rule rounded-md" data-testid={`cases-kb-${a.id}`}>
                  <summary className="cursor-pointer px-3 py-2 text-[12.5px] font-medium text-brand-ink">
                    {a.title}
                  </summary>
                  <div className="px-3 py-2 text-[12px] text-brand-ink whitespace-pre-wrap border-t border-brand-rule">
                    {a.body}
                  </div>
                </details>
              ))}
            </div>
            <div className="flex justify-between gap-2 pt-1">
              <button
                className="btn-outline text-[12px] h-8"
                onClick={() => submit(true)}
                disabled={busy}
                data-testid="cases-kb-answered"
              >
                Answered — no case needed
              </button>
              <button
                className="btn-violet text-[12px] h-8"
                onClick={() => submit(false)}
                disabled={busy}
                data-testid="cases-kb-still-raise"
              >
                Still need help — raise case
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────

export default function Cases() {
  const { companyId } = useParams();
  const [params, setParams] = useSearchParams();
  const { has } = useCompany();

  const [rows, setRows] = React.useState(null);
  const [analytics, setAnalytics] = React.useState(null);
  const [aLoading, setALoading] = React.useState(true);
  const [status, setStatus] = React.useState("");
  const [severity, setSeverity] = React.useState("");
  const [assignee, setAssignee] = React.useState("");
  const [includeClosed, setIncludeClosed] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [openId, setOpenId] = React.useState(params.get("open") || null);
  const [raiseOpen, setRaiseOpen] = React.useState(false);
  const [wbToken, setWbToken] = React.useState(null);

  const canManage = has && (has("cases.manage") || has("cases.confidential"));
  const canManageKB = has && has("kb.manage");
  const [activeTab, setActiveTab] = React.useState("cases");

  const loadList = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/companies/${companyId}/cases`, {
        params: {
          status: status || undefined,
          severity: severity || undefined,
          assignee: assignee || undefined,
          include_closed: includeClosed || undefined,
        },
      });
      setRows(data.cases);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't load cases");
      setRows([]);
    }
  }, [companyId, status, severity, assignee, includeClosed]);

  const loadAnalytics = React.useCallback(async () => {
    if (!canManage) { setALoading(false); return; }
    setALoading(true);
    try {
      const { data } = await api.get(`/companies/${companyId}/cases-analytics`);
      setAnalytics(data);
    } catch {
      setAnalytics(null);
    } finally { setALoading(false); }
  }, [companyId, canManage]);

  React.useEffect(() => { loadList(); }, [loadList]);
  React.useEffect(() => { loadAnalytics(); }, [loadAnalytics]);

  // Live refresh on rule events
  React.useEffect(() => {
    const on = (e) => {
      const t = e.detail?.type || "";
      if (t.startsWith("case.") || t.startsWith("rule_event.case_")) {
        loadList(); loadAnalytics();
      }
    };
    window.addEventListener("fable:event", on);
    return () => window.removeEventListener("fable:event", on);
  }, [loadList, loadAnalytics]);

  const filtered = React.useMemo(() => {
    if (!rows) return null;
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      (r.summary || "").toLowerCase().includes(s) ||
      (r.number || "").toLowerCase().includes(s) ||
      (r.type || "").toLowerCase().includes(s)
    );
  }, [rows, search]);

  function openCase(row) {
    setOpenId(row.id);
    setParams({ open: row.id });
  }
  function closeCase() {
    setOpenId(null);
    const next = new URLSearchParams(params);
    next.delete("open");
    setParams(next);
  }

  async function issueWbToken(rotate = false) {
    try {
      const { data } = await api.post(`/companies/${companyId}/hr/whistleblow-token`, { rotate });
      setWbToken(data.token);
      const url = `${window.location.origin}/report/${data.token}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success(rotate ? "Token rotated + link copied" : "Link copied to clipboard");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't issue token");
    }
  }

  return (
    <div className="px-4 md:px-6 py-4 md:py-6 max-w-[1200px]" data-testid="cases-page">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="h-8 w-8 rounded-md bg-[#EDE9FE] text-brand-violet flex items-center justify-center">
          <FolderOpen size={17} />
        </div>
        <div>
          <div className="text-[19px] font-bold tracking-tight text-brand-ink">Cases</div>
          <div className="text-[11.5px] text-brand-muted">Grievances, disputes, safeguarding, whistleblowing.</div>
        </div>
        <div className="flex-1" />
        {canManage && (
          <button
            className="btn-outline text-[12px] h-8"
            onClick={() => issueWbToken(false)}
            data-testid="cases-issue-wb-token"
            title="Copy the anonymous whistleblowing link to your clipboard"
          >
            <ShieldAlert size={12} /> Anonymous reporting link
          </button>
        )}
        <button
          className="btn-violet text-[12px] h-8"
          onClick={() => setRaiseOpen(true)}
          data-testid="cases-raise-btn"
        >
          Raise a case
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-3" role="tablist" data-testid="cases-tabs">
        <button
          className={`chip mono-num text-[11px] ${activeTab === "cases" ? "chip-violet" : ""}`}
          onClick={() => setActiveTab("cases")}
          role="tab" aria-selected={activeTab === "cases"}
          data-testid="cases-tab-cases"
        >
          Cases
        </button>
        <button
          className={`chip mono-num text-[11px] ${activeTab === "kb" ? "chip-violet" : ""}`}
          onClick={() => setActiveTab("kb")}
          role="tab" aria-selected={activeTab === "kb"}
          data-testid="cases-tab-kb"
        >
          Knowledge base
        </button>
      </div>

      {activeTab === "kb" ? (
        <KBTab companyId={companyId} canManage={canManageKB} />
      ) : (
        <>
      {canManage && <AnalyticsStrip analytics={analytics} loading={aLoading} />}

      <div className="card-panel mb-3">
        <div className="px-3 py-2 flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-brand-muted" />
            <input
              className="field-input !h-8 !w-56 !pl-7 text-[12px]"
              placeholder="Search cases…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="cases-search"
            />
          </div>
          <select className="field-input !h-8 !w-36 text-[12px]" value={status} onChange={(e) => setStatus(e.target.value)}
            data-testid="cases-filter-status">
            <option value="">Any status</option>
            <option value="open">Open</option>
            <option value="in_progress">In progress</option>
            <option value="waiting_on_employee">Waiting on employee</option>
            <option value="waiting_on_third_party">Waiting on third party</option>
            <option value="resolved">Resolved</option>
          </select>
          <select className="field-input !h-8 !w-32 text-[12px]" value={severity} onChange={(e) => setSeverity(e.target.value)}
            data-testid="cases-filter-severity">
            <option value="">Any severity</option>
            <option value="P1">P1</option>
            <option value="P2">P2</option>
            <option value="P3">P3</option>
            <option value="P4">P4</option>
          </select>
          {canManage && (
            <select className="field-input !h-8 !w-36 text-[12px]" value={assignee} onChange={(e) => setAssignee(e.target.value)}
              data-testid="cases-filter-assignee">
              <option value="">Anyone</option>
              <option value="mine">Assigned to me</option>
              <option value="unassigned">Unassigned</option>
            </select>
          )}
          <label className="inline-flex items-center gap-1.5 text-[11.5px] text-brand-muted cursor-pointer">
            <input type="checkbox" checked={includeClosed} onChange={(e) => setIncludeClosed(e.target.checked)}
              data-testid="cases-filter-closed" />
            Show closed
          </label>
          <div className="flex-1" />
          <span className="text-[11px] text-brand-muted">
            {filtered ? `${filtered.length} shown` : "…"}
          </span>
        </div>
      </div>

      <div className="card-panel">
        <div className="px-3 py-2 border-b border-brand-rule grid grid-cols-[110px_60px_1fr_140px_130px] items-center gap-3 text-[10.5px] uppercase tracking-wider text-brand-muted">
          <span>Number</span>
          <span>Sev</span>
          <span>Summary</span>
          <span>Status</span>
          <span className="justify-self-end">SLA</span>
        </div>
        {rows == null ? (
          <div className="p-4 space-y-2" data-testid="cases-list-loading">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-9 rounded bg-brand-hush animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-center" data-testid="cases-empty">
            <div className="text-[13.5px] font-semibold text-brand-ink">No cases match this view</div>
            <div className="text-[12px] text-brand-muted mt-1 mb-3">Try clearing filters, or raise a new case if this is a new issue.</div>
            <button className="btn-violet text-[12px] h-8" onClick={() => setRaiseOpen(true)}>Raise a case</button>
          </div>
        ) : (
          filtered.map((r) => <CaseRow key={r.id} row={r} onOpen={openCase} />)
        )}
      </div>

      {wbToken && (
        <div className="mt-3 card-panel">
          <div className="px-3 py-2 text-[11.5px] text-brand-muted">Public anonymous reporting URL</div>
          <div className="px-3 pb-3 flex items-center gap-2">
            <input readOnly value={`${window.location.origin}/report/${wbToken}`}
              className="field-input mono-num !h-8 flex-1 text-[12px]" data-testid="cases-wb-url" />
            <button className="btn-outline text-[11.5px] h-8"
              onClick={() => issueWbToken(true)}
              data-testid="cases-wb-rotate">Rotate</button>
          </div>
        </div>
      )}
      </>
      )}

      <CaseDrawer
        companyId={companyId}
        caseId={openId}
        open={!!openId}
        onClose={closeCase}
        onChanged={() => { loadList(); loadAnalytics(); }}
        has={has}
      />

      <RaiseDialog
        companyId={companyId}
        open={raiseOpen}
        onClose={() => setRaiseOpen(false)}
        onCreated={(c) => { loadList(); loadAnalytics(); setOpenId(c.id); }}
      />
    </div>
  );


// ─────────────────────────────────────────────────────────────
// KB tab — list + create + edit + delete
// ─────────────────────────────────────────────────────────────

function KBTab({ companyId, canManage }) {
  const [rows, setRows] = React.useState(null);
  const [q, setQ] = React.useState("");
  const [editing, setEditing] = React.useState(null); // article being edited or "new"

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/companies/${companyId}/kb`, { params: { q: q || undefined } });
      setRows(data.articles || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't load KB");
      setRows([]);
    }
  }, [companyId, q]);
  React.useEffect(() => { load(); }, [load]);

  async function save(payload, id) {
    try {
      if (id) await api.patch(`/companies/${companyId}/kb/${id}`, payload);
      else    await api.post(`/companies/${companyId}/kb`, payload);
      toast.success(id ? "Saved" : "Article created");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't save");
    }
  }
  async function remove(id) {
    if (!window.confirm("Delete this article?")) return;
    try {
      await api.delete(`/companies/${companyId}/kb/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't delete");
    }
  }

  return (
    <div data-testid="cases-kb-tab">
      <div className="card-panel mb-3">
        <div className="px-3 py-2 flex flex-wrap items-center gap-2">
          <div className="relative flex-1">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-brand-muted" />
            <input
              className="field-input !h-8 !pl-7 text-[12px]"
              placeholder="Search articles…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="cases-kb-search"
            />
          </div>
          {canManage && (
            <button
              className="btn-violet text-[12px] h-8"
              onClick={() => setEditing({ title: "", body: "", tags: "", case_types: "" })}
              data-testid="cases-kb-new"
            >
              <Plus size={12} /> New article
            </button>
          )}
        </div>
      </div>

      <div className="card-panel">
        {rows == null ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-10 rounded bg-brand-hush animate-pulse" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center" data-testid="cases-kb-empty">
            <BookOpen size={20} className="mx-auto text-brand-muted mb-2" />
            <div className="text-[13.5px] font-semibold text-brand-ink">No articles yet</div>
            <div className="text-[11.5px] text-brand-muted mt-1 mb-3">
              Articles surface as top-of-form deflections when someone tries to raise a case.
            </div>
            {canManage && (
              <button className="btn-violet h-8 text-[12px]" onClick={() => setEditing({ title: "", body: "", tags: "", case_types: "" })}>
                Write your first article
              </button>
            )}
          </div>
        ) : (
          rows.map((a) => (
            <details key={a.id} className="border-b border-brand-rule last:border-0" data-testid={`cases-kb-row-${a.id}`}>
              <summary className="cursor-pointer px-3 py-2 flex items-center gap-2 hover:bg-brand-hush">
                <BookOpen size={12} className="text-brand-muted shrink-0" />
                <span className="text-[13px] font-medium text-brand-ink truncate flex-1">{a.title}</span>
                {(a.tags || []).slice(0, 3).map((t) => (
                  <span key={t} className="chip mono-num text-[9.5px]">{t}</span>
                ))}
                {canManage && (
                  <span className="flex gap-1">
                    <button
                      className="btn-ghost !h-6 !w-6 !p-0"
                      onClick={(e) => { e.preventDefault(); setEditing({ ...a, tags: (a.tags || []).join(","), case_types: (a.case_types || []).join(",") }); }}
                      title="Edit"
                      data-testid={`cases-kb-edit-${a.id}`}
                    >✎</button>
                    <button
                      className="btn-ghost !h-6 !w-6 !p-0 text-brand-coral"
                      onClick={(e) => { e.preventDefault(); remove(a.id); }}
                      title="Delete"
                      data-testid={`cases-kb-delete-${a.id}`}
                    ><Trash2 size={11} /></button>
                  </span>
                )}
              </summary>
              <div className="px-4 py-3 text-[12.5px] text-brand-ink whitespace-pre-wrap border-t border-brand-rule">
                {a.body}
              </div>
            </details>
          ))
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-start justify-center pt-16 px-4" role="dialog" aria-modal="true">
          <div className="bg-card rounded-lg shadow-card w-full max-w-2xl" data-testid="cases-kb-dialog">
            <div className="px-4 py-3 border-b border-brand-rule flex items-center justify-between">
              <div className="text-[14px] font-semibold text-brand-ink">
                {editing.id ? "Edit article" : "New article"}
              </div>
              <button onClick={() => setEditing(null)} className="btn-ghost h-7 w-7 !p-0" aria-label="Close">
                <X size={14} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <label className="block">
                <span className="text-[11px] text-brand-muted">Title</span>
                <input
                  className="field-input mt-1"
                  value={editing.title}
                  onChange={(e) => setEditing((v) => ({ ...v, title: e.target.value }))}
                  maxLength={140} autoFocus
                  data-testid="cases-kb-form-title"
                />
              </label>
              <label className="block">
                <span className="text-[11px] text-brand-muted">Body (markdown-lite)</span>
                <textarea
                  className="field-input mt-1 min-h-[200px]"
                  value={editing.body}
                  onChange={(e) => setEditing((v) => ({ ...v, body: e.target.value }))}
                  maxLength={20000}
                  data-testid="cases-kb-form-body"
                />
              </label>
              <label className="block">
                <span className="text-[11px] text-brand-muted">Tags (comma-separated)</span>
                <input
                  className="field-input mt-1"
                  value={editing.tags}
                  onChange={(e) => setEditing((v) => ({ ...v, tags: e.target.value }))}
                  placeholder="pay, payslip, gross"
                  data-testid="cases-kb-form-tags"
                />
              </label>
              <label className="block">
                <span className="text-[11px] text-brand-muted">Case types (comma-separated)</span>
                <input
                  className="field-input mt-1"
                  value={editing.case_types}
                  onChange={(e) => setEditing((v) => ({ ...v, case_types: e.target.value }))}
                  placeholder="pay_query, timesheet_dispute"
                  data-testid="cases-kb-form-types"
                />
              </label>
              <div className="flex justify-end gap-2">
                <button className="btn-outline text-[12px] h-8" onClick={() => setEditing(null)}>Cancel</button>
                <button
                  className="btn-violet text-[12px] h-8"
                  onClick={() => save({
                    title: editing.title.trim(),
                    body: editing.body,
                    tags: editing.tags.split(",").map((s) => s.trim()).filter(Boolean),
                    case_types: editing.case_types.split(",").map((s) => s.trim()).filter(Boolean),
                  }, editing.id)}
                  disabled={editing.title.trim().length < 3 || editing.body.trim().length < 10}
                  data-testid="cases-kb-form-save"
                >
                  {editing.id ? "Save" : "Create"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

}
